/**
 * Stredná časť obrazovky – polievky a hlavné jedlá v GRID layoute.
 * Landscape: 2 stĺpce (polievky | hlavné jedlá), dodržiava šírku a výšku obrazovky.
 * Portrait: 3 produkty v riadku v každej sekcii.
 * orientation: "landscape" | "portrait" – responzívne rozloženie.
 */
import React from "react";
import { SoupIcon, MealIcon } from "../components/SectionIcons";
import ProductCard from "./ProductCard";
import "./DailyMenuContentGrid.css";

const PORTRAIT_COLUMNS = 3;
const LANDSCAPE_SOUPS_COLUMNS = 2;  /* polievky: 2 v riadku */
const LANDSCAPE_MEALS_COLUMNS = 4;  /* hlavné jedlá: 4 v riadku */

export default function DailyMenuContentGrid({
  orientation = "landscape",
  soups = [],
  meals = [],
  locale,
  defaultLocale,
  placeholderImageSoup,
  placeholderImageMeal,
  soldOutLabel = "",
  classNamePrefix = "daily-menu-screen",
  soupsSingleRow = false,
  mealsSingleRow = false,
  showDescription = true,
  showPrices = true,
}) {
  const prefix = classNamePrefix || "daily-menu-screen";
  const soupsLabel = locale?.startsWith("sk") ? "Polievky" : "Soups";
  const mealsLabel = locale?.startsWith("sk") ? "Hlavné jedlá" : "Meals";
  const sectionTitleClass = `${prefix}__section-title`;
  const sectionIconClass = `${prefix}__section-icon`;
  const gridClass =
    orientation === "portrait"
      ? "daily-menu-content-grid daily-menu-content-grid--portrait"
      : "daily-menu-content-grid daily-menu-content-grid--landscape";
  const soupsColumns = orientation === "landscape" ? LANDSCAPE_SOUPS_COLUMNS : PORTRAIT_COLUMNS;
  const mealsColumns = orientation === "landscape" ? LANDSCAPE_MEALS_COLUMNS : PORTRAIT_COLUMNS;
  const soupsSectionClass =
    orientation === "landscape" && soupsSingleRow
      ? "daily-menu-content-grid__section daily-menu-content-grid__section--soups-single-row"
      : "daily-menu-content-grid__section";
  const mealsSectionClass =
    orientation === "landscape" && mealsSingleRow
      ? "daily-menu-content-grid__section daily-menu-content-grid__section--meals-single-row"
      : "daily-menu-content-grid__section";

  const soupCardProps = {
    locale,
    defaultLocale,
    placeholderImage: placeholderImageSoup,
    soldOutLabel,
    classNamePrefix: prefix,
    badgeOnImage: true,
    showDescription,
    showPrices,
  };
  const mealCardProps = {
    locale,
    defaultLocale,
    placeholderImage: placeholderImageMeal,
    soldOutLabel,
    classNamePrefix: prefix,
    badgeOnImage: true,
    showDescription,
    showPrices,
  };

  const hasSoups = soups && soups.length > 0;
  const hasMeals = meals && meals.length > 0;
  const onlySoups = hasSoups && !hasMeals;
  const onlyMeals = hasMeals && !hasSoups;
  const wrapperClass = [
    gridClass,
    onlySoups && "daily-menu-content-grid--soups-only",
    onlyMeals && "daily-menu-content-grid--meals-only",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={wrapperClass}>
      {hasSoups && (
        <section className={soupsSectionClass}>
          <h2 className={sectionTitleClass}>
            <SoupIcon className={sectionIconClass} size={orientation === "portrait" ? 40 : 36} />
            <span>{soupsLabel}</span>
          </h2>
          <div
            className="daily-menu-content-grid__items"
            style={{ "--grid-columns": soupsColumns }}
          >
            {soups.map((item) => (
              <ProductCard
                key={item?.id ?? item?.title}
                item={item}
                {...soupCardProps}
              />
            ))}
          </div>
        </section>
      )}
      {hasMeals && (
        <section className={mealsSectionClass}>
          <h2 className={sectionTitleClass}>
            <MealIcon className={sectionIconClass} size={orientation === "portrait" ? 40 : 36} />
            <span>{mealsLabel}</span>
          </h2>
          <div
            className="daily-menu-content-grid__items"
            style={{ "--grid-columns": mealsColumns }}
          >
            {meals.map((item) => (
              <ProductCard
                key={item?.id ?? item?.title}
                item={item}
                {...mealCardProps}
              />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
